import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { tiktokAPI } from '../services/api';
import { useAuth } from './AuthContext';

// Types
export interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string | string[];
    borderWidth?: number;
    tension?: number;
    pointBackgroundColor?: string;
    pointBorderColor?: string;
    pointRadius?: number;
    pointHoverRadius?: number;
  }[];
}

export interface AIInsight {
  type: 'insight' | 'prediction' | 'warning' | 'recommendation';
  title: string;
  message: string;
  icon: string;
  color: string;
}

export interface AIAnalysis {
  insights: AIInsight[];
}

export interface AccountData {
  username: string;
  displayName?: string;
  followers: number;
  following: number;
  videos: number;
  profilePicture: string;
  bio: string;
  website: string;
  isVerified: boolean;
  totalLikes: number;
  totalViews: number;
}

export interface VideoData {
  id: string | number;
  thumbnail: string;
  videoUrl?: string;
  caption: string;
  likes: number;
  comments: number;
  shares: number;
  views: number;
  engagement: number;
  postedAt: string;
  duration: number;
  hashtags: string[];
}

export interface ProblemData {
  title: string;
  count: number;
  recommendation: string;
}

export interface TikTokState {
  account: AccountData | null;
  recentVideos: VideoData[];
  topVideos: VideoData[];
  engagement: ChartData | null;
  reach: ChartData | null;
  hashtags: ChartData | null;
  contentPerformance: ChartData | null;
  sentiment: ChartData | null;
  problems: ProblemData[];
  aiAnalysis: {
    [key: string]: AIAnalysis | null;
  };
  loading: {
    account: boolean;
    recentVideos: boolean;
    topVideos: boolean;
    engagement: boolean;
    reach: boolean;
    hashtags: boolean;
    contentPerformance: boolean;
    sentiment: boolean;
    problems: boolean;
    aiAnalysis: {
      [key: string]: boolean;
    };
  };
  error: {
    account: string | null;
    recentVideos: string | null;
    topVideos: string | null;
    engagement: string | null;
    reach: string | null;
    hashtags: string | null;
    contentPerformance: string | null;
    sentiment: string | null;
    problems: string | null;
    aiAnalysis: {
      [key: string]: string | null;
    };
  };
}

export type TikTokAction =
  | { type: 'FETCH_ACCOUNT_START' }
  | { type: 'FETCH_ACCOUNT_SUCCESS'; payload: AccountData }
  | { type: 'FETCH_ACCOUNT_ERROR'; payload: string }
  | { type: 'FETCH_RECENT_VIDEOS_START' }
  | { type: 'FETCH_RECENT_VIDEOS_SUCCESS'; payload: VideoData[] }
  | { type: 'FETCH_RECENT_VIDEOS_ERROR'; payload: string }
  | { type: 'FETCH_TOP_VIDEOS_START' }
  | { type: 'FETCH_TOP_VIDEOS_SUCCESS'; payload: VideoData[] }
  | { type: 'FETCH_TOP_VIDEOS_ERROR'; payload: string }
  | { type: 'FETCH_ENGAGEMENT_START' }
  | { type: 'FETCH_ENGAGEMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_ENGAGEMENT_ERROR'; payload: string }
  | { type: 'FETCH_REACH_START' }
  | { type: 'FETCH_REACH_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_REACH_ERROR'; payload: string }
  | { type: 'FETCH_HASHTAGS_START' }
  | { type: 'FETCH_HASHTAGS_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_HASHTAGS_ERROR'; payload: string }
  | { type: 'FETCH_CONTENT_PERFORMANCE_START' }
  | { type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_CONTENT_PERFORMANCE_ERROR'; payload: string }
  | { type: 'FETCH_SENTIMENT_START' }
  | { type: 'FETCH_SENTIMENT_SUCCESS'; payload: ChartData }
  | { type: 'FETCH_SENTIMENT_ERROR'; payload: string }
  | { type: 'FETCH_PROBLEMS_START' }
  | { type: 'FETCH_PROBLEMS_SUCCESS'; payload: ProblemData[] }
  | { type: 'FETCH_PROBLEMS_ERROR'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_START'; payload: string }
  | { type: 'FETCH_AI_ANALYSIS_SUCCESS'; payload: { chartType: string; data: AIAnalysis } }
  | { type: 'FETCH_AI_ANALYSIS_ERROR'; payload: { chartType: string; error: string } };

const initialState: TikTokState = {
  account: null,
  recentVideos: [],
  topVideos: [],
  engagement: null,
  reach: null,
  hashtags: null,
  contentPerformance: null,
  sentiment: null,
  problems: [],
  aiAnalysis: {},
  loading: {
    account: false,
    recentVideos: false,
    topVideos: false,
    engagement: false,
    reach: false,
    hashtags: false,
    contentPerformance: false,
    sentiment: false,
    problems: false,
    aiAnalysis: {},
  },
  error: {
    account: null,
    recentVideos: null,
    topVideos: null,
    engagement: null,
    reach: null,
    hashtags: null,
    contentPerformance: null,
    sentiment: null,
    problems: null,
    aiAnalysis: {},
  },
};

const tiktokReducer = (state: TikTokState, action: TikTokAction): TikTokState => {
  switch (action.type) {
    case 'FETCH_ACCOUNT_START':
      return {
        ...state,
        loading: { ...state.loading, account: true },
        error: { ...state.error, account: null },
      };
    case 'FETCH_ACCOUNT_SUCCESS':
      return {
        ...state,
        account: action.payload,
        loading: { ...state.loading, account: false },
      };
    case 'FETCH_ACCOUNT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, account: false },
        error: { ...state.error, account: action.payload },
      };
    case 'FETCH_RECENT_VIDEOS_START':
      return {
        ...state,
        loading: { ...state.loading, recentVideos: true },
        error: { ...state.error, recentVideos: null },
      };
    case 'FETCH_RECENT_VIDEOS_SUCCESS':
      return {
        ...state,
        recentVideos: action.payload,
        loading: { ...state.loading, recentVideos: false },
      };
    case 'FETCH_RECENT_VIDEOS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, recentVideos: false },
        error: { ...state.error, recentVideos: action.payload },
      };
    case 'FETCH_TOP_VIDEOS_START':
      return {
        ...state,
        loading: { ...state.loading, topVideos: true },
        error: { ...state.error, topVideos: null },
      };
    case 'FETCH_TOP_VIDEOS_SUCCESS':
      return {
        ...state,
        topVideos: action.payload,
        loading: { ...state.loading, topVideos: false },
      };
    case 'FETCH_TOP_VIDEOS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, topVideos: false },
        error: { ...state.error, topVideos: action.payload },
      };
    case 'FETCH_ENGAGEMENT_START':
      return {
        ...state,
        loading: { ...state.loading, engagement: true },
        error: { ...state.error, engagement: null },
      };
    case 'FETCH_ENGAGEMENT_SUCCESS':
      return {
        ...state,
        engagement: action.payload,
        loading: { ...state.loading, engagement: false },
      };
    case 'FETCH_ENGAGEMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, engagement: false },
        error: { ...state.error, engagement: action.payload },
      };
    case 'FETCH_REACH_START':
      return {
        ...state,
        loading: { ...state.loading, reach: true },
        error: { ...state.error, reach: null },
      };
    case 'FETCH_REACH_SUCCESS':
      return {
        ...state,
        reach: action.payload,
        loading: { ...state.loading, reach: false },
      };
    case 'FETCH_REACH_ERROR':
      return {
        ...state,
        loading: { ...state.loading, reach: false },
        error: { ...state.error, reach: action.payload },
      };
    case 'FETCH_HASHTAGS_START':
      return {
        ...state,
        loading: { ...state.loading, hashtags: true },
        error: { ...state.error, hashtags: null },
      };
    case 'FETCH_HASHTAGS_SUCCESS':
      return {
        ...state,
        hashtags: action.payload,
        loading: { ...state.loading, hashtags: false },
      };
    case 'FETCH_HASHTAGS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, hashtags: false },
        error: { ...state.error, hashtags: action.payload },
      };
    case 'FETCH_CONTENT_PERFORMANCE_START':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: true },
        error: { ...state.error, contentPerformance: null },
      };
    case 'FETCH_CONTENT_PERFORMANCE_SUCCESS':
      return {
        ...state,
        contentPerformance: action.payload,
        loading: { ...state.loading, contentPerformance: false },
      };
    case 'FETCH_CONTENT_PERFORMANCE_ERROR':
      return {
        ...state,
        loading: { ...state.loading, contentPerformance: false },
        error: { ...state.error, contentPerformance: action.payload },
      };
    case 'FETCH_SENTIMENT_START':
      return {
        ...state,
        loading: { ...state.loading, sentiment: true },
        error: { ...state.error, sentiment: null },
      };
    case 'FETCH_SENTIMENT_SUCCESS':
      return {
        ...state,
        sentiment: action.payload,
        loading: { ...state.loading, sentiment: false },
      };
    case 'FETCH_SENTIMENT_ERROR':
      return {
        ...state,
        loading: { ...state.loading, sentiment: false },
        error: { ...state.error, sentiment: action.payload },
      };
    case 'FETCH_PROBLEMS_START':
      return {
        ...state,
        loading: { ...state.loading, problems: true },
        error: { ...state.error, problems: null },
      };
    case 'FETCH_PROBLEMS_SUCCESS':
      return {
        ...state,
        problems: action.payload,
        loading: { ...state.loading, problems: false },
      };
    case 'FETCH_PROBLEMS_ERROR':
      return {
        ...state,
        loading: { ...state.loading, problems: false },
        error: { ...state.error, problems: action.payload },
      };
    case 'FETCH_AI_ANALYSIS_START':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload]: true,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload]: null,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_SUCCESS':
      return {
        ...state,
        aiAnalysis: {
          ...state.aiAnalysis,
          [action.payload.chartType]: action.payload.data,
        },
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
      };
    case 'FETCH_AI_ANALYSIS_ERROR':
      return {
        ...state,
        loading: {
          ...state.loading,
          aiAnalysis: {
            ...state.loading.aiAnalysis,
            [action.payload.chartType]: false,
          },
        },
        error: {
          ...state.error,
          aiAnalysis: {
            ...state.error.aiAnalysis,
            [action.payload.chartType]: action.payload.error,
          },
        },
      };
    default:
      return state;
  }
};

interface TikTokContextType {
  state: TikTokState;
  fetchAccountData: () => Promise<void>;
  fetchRecentVideos: () => Promise<void>;
  fetchTopVideos: () => Promise<void>;
  fetchEngagementData: () => Promise<void>;
  fetchReachData: () => Promise<void>;
  fetchHashtagsData: () => Promise<void>;
  fetchContentPerformanceData: () => Promise<void>;
  fetchSentimentData: () => Promise<void>;
  fetchProblemsData: () => Promise<void>;
  fetchAIAnalysis: (chartType: string) => Promise<void>;
}

const TikTokContext = createContext<TikTokContextType | undefined>(undefined);

export const TikTokProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(tiktokReducer, initialState);
  const { business } = useAuth();

  const fetchAccountData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ACCOUNT_START' });
    try {
      const response = await tiktokAPI.getAccountData(business._id);
      const raw = response.data?.data;
      // Backend response structure:
      // { success, data: { details: { username, displayName, followerCount, followingCount, videoCount, profileImageUrl, isVerified, ... } } }
      const details = raw?.details ?? {};
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxy = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      const normalizedAccount: AccountData = {
        username: details.username ?? '',
        displayName: details.displayName ?? undefined,
        followers: Number(details.followerCount ?? 0),
        following: Number(details.followingCount ?? 0),
        videos: Number(details.videoCount ?? 0),
        profilePicture: toProxy(details.profileImageUrl ?? ''),
        bio: details.bio ?? details.description ?? '',
        website: details.website ?? '',
        isVerified: Boolean(details.isVerified ?? false),
        totalLikes: Number(details.likesReceived ?? 0),
        totalViews: Number(details.totalViews ?? 0),
      };
      
      dispatch({ type: 'FETCH_ACCOUNT_SUCCESS', payload: normalizedAccount });
    } catch (error) {
      dispatch({ type: 'FETCH_ACCOUNT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchRecentVideos = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_RECENT_VIDEOS_START' });
    try {
      const response = await tiktokAPI.getRecentVideos(business._id);
      // Handle both array and single object responses
      let rawVideos: any[] = [];
      if (response.data?.data) {
        rawVideos = Array.isArray(response.data.data) 
          ? response.data.data 
          : [response.data.data];
      } else if (Array.isArray(response.data)) {
        rawVideos = response.data;
      }
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxyImage = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Normalize video data from new API structure
      const videos: VideoData[] = rawVideos.map((video: any, index: number) => {
        if (!video || typeof video !== 'object') {
          return {
            id: index,
            thumbnail: '',
            caption: '',
            likes: 0,
            comments: 0,
            shares: 0,
            views: 0,
            engagement: 0,
            postedAt: '',
            duration: 0,
            hashtags: [],
          };
        }

        // Get thumbnail from coverImageUrl or videoDetails.coverUrl
        const thumbnail = video.coverImageUrl || video.videoDetails?.coverUrl || '';
        
        // Get caption from text
        const caption = video.text || '';
        
        // Get likes from likes or diggCount
        const likes = Number(video.likes ?? video.diggCount ?? 0);
        
        // Get comments
        const comments = Number(video.comments ?? 0);
        
        // Get shares from shares or shareCount
        const shares = Number(video.shares ?? video.shareCount ?? 0);
        
        // Get views from viewCount
        const views = Number(video.viewCount ?? 0);
        
        // Calculate engagement percentage
        const totalEngagement = likes + comments + shares;
        const engagement = views > 0 ? Math.round((totalEngagement / views) * 100 * 100) / 100 : 0;
        
        // Get postedAt from createdAt
        const postedAt = video.createdAt ? new Date(video.createdAt).toLocaleDateString('tr-TR') : '';
        
        // Get duration (in seconds)
        const duration = Number(video.duration ?? video.videoDetails?.duration ?? 0);
        
        // Get hashtags array
        const hashtags = Array.isArray(video.hashtags) ? video.hashtags : [];
        
        // Get id from id or _id
        const id = video.id || video._id || index;

        return {
          id,
          thumbnail: toProxyImage(thumbnail),
          videoUrl: undefined,
          caption,
          likes,
          comments,
          shares,
          views,
          engagement,
          postedAt,
          duration,
          hashtags,
        };
      });
      
      dispatch({ type: 'FETCH_RECENT_VIDEOS_SUCCESS', payload: videos });
    } catch (error) {
      dispatch({ type: 'FETCH_RECENT_VIDEOS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchTopVideos = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_TOP_VIDEOS_START' });
    try {
      const response = await tiktokAPI.getTopVideos(business._id);
      // Handle both array and single object responses
      let rawVideos: any[] = [];
      if (response.data?.data) {
        rawVideos = Array.isArray(response.data.data) 
          ? response.data.data 
          : [response.data.data];
      } else if (Array.isArray(response.data)) {
        rawVideos = response.data;
      }
      
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
      const toProxyImage = (url?: string) => (url ? `${baseUrl}/proxy-image?url=${encodeURIComponent(url)}` : '');
      
      // Normalize video data from new API structure
      const videos: VideoData[] = rawVideos.map((video: any, index: number) => {
        if (!video || typeof video !== 'object') {
          return {
            id: index,
            thumbnail: '',
            caption: '',
            likes: 0,
            comments: 0,
            shares: 0,
            views: 0,
            engagement: 0,
            postedAt: '',
            duration: 0,
            hashtags: [],
          };
        }

        // Get thumbnail from coverImageUrl or videoDetails.coverUrl
        const thumbnail = video.coverImageUrl || video.videoDetails?.coverUrl || '';
        
        // Get caption from text
        const caption = video.text || '';
        
        // Get likes from likes or diggCount
        const likes = Number(video.likes ?? video.diggCount ?? 0);
        
        // Get comments
        const comments = Number(video.comments ?? 0);
        
        // Get shares from shares or shareCount
        const shares = Number(video.shares ?? video.shareCount ?? 0);
        
        // Get views from viewCount
        const views = Number(video.viewCount ?? 0);
        
        // Calculate engagement percentage
        const totalEngagement = likes + comments + shares;
        const engagement = views > 0 ? Math.round((totalEngagement / views) * 100 * 100) / 100 : 0;
        
        // Get postedAt from createdAt
        const postedAt = video.createdAt ? new Date(video.createdAt).toLocaleDateString('tr-TR') : '';
        
        // Get duration (in seconds)
        const duration = Number(video.duration ?? video.videoDetails?.duration ?? 0);
        
        // Get hashtags array
        const hashtags = Array.isArray(video.hashtags) ? video.hashtags : [];
        
        // Get id from id or _id
        const id = video.id || video._id || index;

        return {
          id,
          thumbnail: toProxyImage(thumbnail),
          videoUrl: undefined,
          caption,
          likes,
          comments,
          shares,
          views,
          engagement,
          postedAt,
          duration,
          hashtags,
        };
      });
      
      dispatch({ type: 'FETCH_TOP_VIDEOS_SUCCESS', payload: videos });
    } catch (error) {
      dispatch({ type: 'FETCH_TOP_VIDEOS_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchEngagementData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_ENGAGEMENT_START' });
    try {
      const response = await tiktokAPI.getEngagementData(business._id);
      dispatch({ type: 'FETCH_ENGAGEMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_ENGAGEMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchReachData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_REACH_START' });
    try {
      const response = await tiktokAPI.getReachData(business._id);
      dispatch({ type: 'FETCH_REACH_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_REACH_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchContentPerformanceData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_START' });
    try {
      const response = await tiktokAPI.getContentPerformanceData(business._id);
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_CONTENT_PERFORMANCE_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchSentimentData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_SENTIMENT_START' });
    try {
      const response = await tiktokAPI.getSentimentData(business._id);
      dispatch({ type: 'FETCH_SENTIMENT_SUCCESS', payload: response.data.data });
    } catch (error) {
      dispatch({ type: 'FETCH_SENTIMENT_ERROR', payload: 'Veri yüklenirken hata oluştu' });
    }
  }, [business?._id]);

  const fetchHashtagsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_HASHTAGS_START' });
    try {
      const response = await tiktokAPI.getHashtagsData(business._id);
      dispatch({ type: 'FETCH_HASHTAGS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_HASHTAGS_ERROR', payload: 'Hashtag verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchProblemsData = useCallback(async () => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_PROBLEMS_START' });
    try {
      const response = await tiktokAPI.getProblemsData(business._id);
      dispatch({ type: 'FETCH_PROBLEMS_SUCCESS', payload: response.data.data });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'PREPARING' });
      } else {
        dispatch({ type: 'FETCH_PROBLEMS_ERROR', payload: 'Problem verileri yüklenirken hata oluştu' });
      }
    }
  }, [business?._id]);

  const fetchAIAnalysis = useCallback(async (chartType: string) => {
    if (!business?._id) return;
    
    dispatch({ type: 'FETCH_AI_ANALYSIS_START', payload: chartType });
    try {
      const response = await tiktokAPI.getAIAnalysis(business._id, chartType);
      dispatch({
        type: 'FETCH_AI_ANALYSIS_SUCCESS',
        payload: { chartType, data: response.data.data },
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'PREPARING' },
        });
      } else {
        dispatch({
          type: 'FETCH_AI_ANALYSIS_ERROR',
          payload: { chartType, error: 'AI analizi yüklenirken hata oluştu' },
        });
      }
    }
  }, [business?._id]);

  return (
    <TikTokContext.Provider
      value={{
        state,
        fetchAccountData,
        fetchRecentVideos,
        fetchTopVideos,
        fetchEngagementData,
        fetchReachData,
        fetchHashtagsData,
        fetchContentPerformanceData,
        fetchSentimentData,
        fetchProblemsData,
        fetchAIAnalysis,
      }}
    >
      {children}
    </TikTokContext.Provider>
  );
};

export const useTikTok = (): TikTokContextType => {
  const context = useContext(TikTokContext);
  if (context === undefined) {
    throw new Error('useTikTok must be used within a TikTokProvider');
  }
  return context;
};
